import { useMemo } from "react";
import type {
	ClientPropertyItemResponseDTO,
	PortfolioListItemResponseDTO,
	PortfolioListReportingSummaryResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	PORTFOLIO_SORT,
	type PortfolioSortKey,
} from "web_myplann/Portfolio/Components/Redesign/Filter/portfolioSortKey";
import { portfolioCategoryOf } from "web_myplann/Portfolio/Helpers/portfolioCategoryMapping";
import { sortItems } from "web_myplann/Portfolio/Helpers/portfolioCategorySort";
import {
	type CategoryItem,
	PORTFOLIO_CATEGORY_ORDER,
	type PortfolioCategory,
	type PortfolioCategoryId,
	PortfolioCategoryIds,
} from "web_myplann/Portfolio/Types/portfolioCategory";
import { PortfolioItemKind } from "web_myplann/Portfolio/Types/portfolioItemKind";

type Options = {
	categories?: Set<PortfolioCategoryId>;
	sort?: PortfolioSortKey;
	sortDesc?: boolean;
	summaryData?: PortfolioListReportingSummaryResponseDTO[];
};

export const usePortfolioCategories = (
	portfolioData: PortfolioListItemResponseDTO[],
	propertyData: ClientPropertyItemResponseDTO[],
	{
		categories,
		sort = PORTFOLIO_SORT.name,
		sortDesc = false,
		summaryData,
	}: Options = {},
): PortfolioCategory[] => {
	return useMemo(() => {
		const buckets = Object.fromEntries(
			PORTFOLIO_CATEGORY_ORDER.map((id) => [id, [] as CategoryItem[]]),
		) as Record<PortfolioCategoryId, CategoryItem[]>;

		for (const data of portfolioData) {
			buckets[portfolioCategoryOf(data.domain)].push({
				kind: PortfolioItemKind.Portfolio,
				data,
			});
		}
		for (const data of propertyData) {
			buckets[PortfolioCategoryIds.property].push({
				kind: PortfolioItemKind.Property,
				data,
			});
		}

		return PORTFOLIO_CATEGORY_ORDER.filter(
			(id) => !categories || categories.has(id),
		)
			.map((id) => ({
				id,
				items: sortItems(buckets[id], sort, sortDesc, summaryData),
			}))
			.filter((category) => category.items.length > 0);
	}, [portfolioData, propertyData, categories, sort, sortDesc, summaryData]);
};
